/**
 * BOSS Reports — collapsing a side-by-side grid to a row field, with group sums.
 *
 * The pivot-grid gesture every desktop user knows: collapse to STORENAME and see one row per store
 * with the measures summed, the fields to the right blank, and — as DevExpress does — a TOTAL row
 * plus a subtotal row for each level above. Rows stay side by side. Nothing leaves the Rows area.
 *
 * WHAT THE ENGINE CAN AND CANNOT DO (measured). In side-by-side mode (`group_rollup_mode: 'flat'`)
 * the engine returns leaf rows only and IGNORES depth: `set_depth(2)` was accepted and changed
 * nothing (422 leaves before and after). In tree mode (`'rollup'`) it honours depth and returns
 * exactly the rows wanted, with true group sums (depth 2 on the same report: 162 rows, QUANTITY
 * 1,566 for TOTAL). But the datagrid's tree formatter emits only each row's LAST path part — the
 * parents live on parent rows — while side by side needs every part on every row, which only the
 * FLAT formatter produces (one formatted cell per path part), and only for leaves.
 *
 * SO: "FLAT PLUGIN, ROLLUP VIEW". The viewer's config stays `'flat'` — the plugin keeps its
 * side-by-side formatter and its `flat-group-rollup-mode` class, and `save()` keeps saying flat —
 * while the plugin READS from a second View this module creates from the same Table with
 * `{ ...config, group_rollup_mode: 'rollup', group_by_depth: d }`. `table.view()` honours
 * `group_by_depth` (measured: 8 rows = TOTAL + 2 regions + 5 stores); `viewer.restore()` does not,
 * which is why the level cannot live in the Perspective config and is persisted in the layout
 * envelope beside the pin lists instead (`collapseToRowField`, by NAME — an index would silently
 * re-point when Rows are reordered).
 *
 * The swap is a Proxy over the model's `_view` that routes reads (`schema`, `column_paths`, …) to
 * the collapsed view — the windowed reads translated to display order and `num_rows` answered
 * with the DISPLAY count, see below — and `expand` / `collapse` / `set_depth` / `delete` to the
 * REAL flat view. That exception is load-bearing: the plugin routes any tree-label header click to its
 * expand/collapse handler with no flat-mode gate, and against the real view the engine ignores it,
 * so a click cannot desynchronise the drawn rows from the persisted level (measured).
 *
 * The flat formatter accepts a short path and yields fewer cells; the rest are padded here. The
 * blank is a fresh `{ toString: () => ' ' }` PER CELL, and every part of that matters:
 *  - an object, because regular-table merges row headers by `===` — colspan into an equal previous
 *    column, rowspan into an equal row above — and identity-unique objects never merge;
 *  - a non-breaking space, because an EMPTY th matches the plugin's ungated `tbody th:empty` rule
 *    (tree connector gradient, 20px cap, pointer-events none — a right-click falls through), while
 *    `trim()` of a nbsp is empty so the cell earns no `psp-tree-label`;
 *  - and ancestor (subtotal) rows get identity-unique wrappers for their present parts too, so a
 *    subtotal row stays its own row instead of being rowspanned into its children; collapse-level
 *    rows keep plain strings so equal siblings rowspan on shared prefixes exactly as flat does.
 *
 * TOTALS BELOW THEIR GROUP. The rollup view returns rows in PRE-order — TOTAL first, each subtotal
 * before its children — and the desktop grid drew them the other way round: each subtotal after its
 * last child, the grand TOTAL last. The plugin's data listener is WINDOWED: per draw it asks the
 * (proxied) view for `[y0, y1)` through `to_columns_string`, and stores what came back on the model
 * (`_ids`, `_last_window`, `_reverse_ids`, …) for its click, edit and selection handlers to index
 * by display row. So the reorder cannot be done by calling the listener several times and
 * stitching — that state would describe only the last call (measured) — and is done INSIDE the
 * Proxy instead: a POST-order permutation is built once per collapsed view from `__ROW_PATH__`
 * alone (`to_columns_string({ start_col: 0, end_col: 0 })` returns only the paths: 0.5 ms at 400
 * rows, 8 ms at 12,400, 67 ms at 69,700 — where the rollup view itself took 1,150 ms), and every
 * windowed read (`to_columns_string`, `to_columns`, `to_json`) has
 * its display window translated to the view rows it needs — sorted, merged into contiguous runs
 * when closer than `RUN_GAP`, one real call per run, stitched back in display order. A subtotal
 * whose whole group fits the window sits right before it in pre-order, so a window is one or two
 * runs; a single span fetch was rejected because TOTAL is view row 0 and the bottom window would
 * span the whole view. The listener still makes exactly one call per draw and its state comes
 * out in display coordinates. Measured: +0.2 ms per listener call; the ~65 ms frame is unchanged.
 * regular-table merges row headers UPWARD only (tbody.ts:120-154), so a subtotal below its children
 * is never merged into them — as long as the `uniq` wrapper on ancestor rows stays. One accepted
 * difference from the desktop grid follows: the parent value is drawn again on the first leaf after
 * each subtotal, because the subtotal's unique cell ends the rowspan. Copy and export are NOT
 * affected: the viewer hands the plugin's `render()` its own real view, never the model's. The
 * order is keyed on (collapsed view, its row count, depth, hidden levels) and rebuilt whenever the
 * config — and so the view — changes or a level's totals are toggled; BOSS tables are fully
 * populated before the viewer sees them (lib/perspective), which is what makes a row-count key
 * safe. Copy/export therefore also ignores hidden levels: it never sees the permutation.
 *
 * Composition: this wrapper sits INSIDE the pinning wrapper (lib/bossRowPinning), so the pin split
 * sees full-length rows exactly as it does today; the datetime zone pin runs in the outer wrapper
 * before the inner call and therefore covers this path too. Captions and the width cap are
 * untouched — `group_by` is still the full list and the plugin keeps the flat class on. The
 * shading of total rows (lib/bossTotalsShading) asks `rowKindAt` what a display row is.
 *
 * Proved against the real 5.3.1 packages: row counts and paths at every level, group sums, blank
 * inertness, no merges, th click inert, a non-prefix pinned set, split_by, sort, theme and settings
 * redraws, deep scroll, expand-all restoring the leaves — 42/42, zero console errors; the
 * permutation separately at depths 1-4 on 59 and 12,000 leaves — 49/49.
 */
import type { ViewerConfig } from './bossLayoutOps'

/** The envelope key, as the server validates and stores it. */
export const COLLAPSE_KEY = 'collapseToRowField'

/**
 * The envelope key for the row fields whose SUBTOTAL rows are switched off — the legacy grid's
 * per-field "Show Totals". A collapsed grid shows one subtotal per group at every level above the
 * collapse level; a STORENUM and a STORENAME level that are one-to-one draw two identical rows, and
 * the user turns one off. The setting is a list of hidden fields, so the default — everything shown,
 * as the desktop tool defaulted — needs no key. Hidden rows are simply left out of the display
 * permutation; the grand TOTAL row is not affected.
 */
export const HIDDEN_TOTALS_KEY = 'hiddenTotalsRowFields'

/** A Perspective view window; only the row bounds matter here, the rest passes through. */
type ViewWindow = { start_row?: number; end_row?: number; [key: string]: unknown }

/** The minimum of Perspective's View this module calls. */
type View = {
    num_rows(): Promise<number>
    delete(): Promise<void>
    to_columns_string(window?: ViewWindow): Promise<string>
    to_columns(window?: ViewWindow): Promise<Record<string, unknown[]>>
    to_json(window?: ViewWindow): Promise<Record<string, unknown>[]>
}

type Table = { view(config: Record<string, unknown>): Promise<View> }

/** What a DISPLAY row of a collapsed grid is. */
export type RowKind = 'leaf' | 'subtotal' | 'total'

/**
 * The display permutation of a collapsed view: `disp[y]` is the VIEW row drawn at display row y
 * (`M` of them — fewer than the view's `N` rows when some levels' subtotals are hidden), `len[v]`
 * the path length of view row v, `depth` the collapse depth (a leaf's path length).
 */
export type RowOrder = { disp: Int32Array; len: Uint8Array; N: number; M: number; depth: number }

const NO_HIDDEN: ReadonlySet<number> = new Set()

/** The view methods whose windows are translated to display order. Exactly these three. */
const TRANSLATED = new Set<PropertyKey>(['to_columns_string', 'to_columns', 'to_json'])
type TranslatedMethod = 'to_columns_string' | 'to_columns' | 'to_json'

/** View rows closer than this are fetched as one run rather than two calls. */
const RUN_GAP = 8

type DatagridModel = {
    _config: { group_by: string[]; group_rollup_mode?: string }
    _view: View
    _table: Table
    _num_rows: number
}

type DataResponse = { row_headers?: unknown[][]; [key: string]: unknown }
type DataListener = (x0: number, y0: number, x1: number, y1: number) => Promise<DataResponse>

type CollapseState = {
    field: string | null
    /** Row fields whose subtotal rows are switched off; read at draw time like `field`. */
    hiddenTotals: readonly string[]
    key: string | null
    /** What `order` was built for — depth and hidden levels — so a toggle rebuilds it. */
    orderKey: string | null
    table: Table | null
    viewPromise: Promise<View> | null
    view: View | null
    real: View | null
    proxy: View | null
    /** The display permutation of `orderView`; null exactly when the rows on screen are not permuted. */
    order: RowOrder | null
    orderView: View | null
}

const stateByViewer = new WeakMap<Element, CollapseState>()

/** Method names that must keep reaching the REAL (flat) view. See the module note. */
const ROUTE_TO_REAL = new Set<PropertyKey>(['expand', 'collapse', 'set_depth', 'delete'])

const NBSP = ' '

/** A blank cell that renders as nothing, never merges, and is not `:empty`. Fresh per cell. */
const blank = (): unknown => ({ toString: () => NBSP })

/**
 * The word a subtotal row's own cell carries after the group's value — "VPO Nelson Total" — so a
 * total row reads as one instead of looking like another detail row. The desktop grid wrote the
 * summary's own name there ("Sum"); "Total" is used for all of them because the Values area can
 * hold an average or a count beside a sum, and it is the word already on the grand TOTAL row.
 */
const TOTAL_WORD = 'Total'

/** Excel's own name for a null group key, so a blank subtotal is not just the bare word. */
const BLANK_KEY = '(blank)'

/** A present value wrapped so it never rowspan/colspan-merges. The text is unchanged. */
const uniq = (value: unknown): unknown =>
    value instanceof HTMLElement ? value : { toString: () => String(value ?? '') }

/**
 * A subtotal row's own cell: the group's value with the summary word beside it.
 *
 * Two things the plugin's formatter hands us that a template string would ruin:
 *  - an HTMLElement, which it returns for a string column carrying a `format` of bold, italics or
 *    link (data_listener/format_cell.ts), and which `String()` renders as "[object HTMLElement]".
 *    That is reachable today: the grid menu offers Format To on a row header, and the entry
 *    survives `restore()` and `save()` for a string row field (measured). The element is kept and
 *    the word appended beside it in a fresh span — regular-table appends an element cell value and
 *    compares cells by identity, so a per-cell span both renders and never merges.
 *  - `null`, which is what a NULL group key formats to. Named rather than left as a bare " Total",
 *    which would read like the grand total row sitting a few lines below it.
 */
function labelled(value: unknown): unknown {
    if (value instanceof HTMLElement) {
        const span = document.createElement('span')
        span.appendChild(value)
        span.appendChild(document.createTextNode(` ${TOTAL_WORD}`))
        return span
    }
    const text = String(value ?? '')
    return uniq(`${text.trim() === '' ? BLANK_KEY : text} ${TOTAL_WORD}`)
}

// ── State ─────────────────────────────────────────────────────────────

/**
 * Sets the field the grid collapses to, or null for fully expanded. Call before the first
 * `restore()` so the first paint is already collapsed; after that, redraw (`redrawForPinChange`).
 */
export function setRowCollapse(viewer: Element, field: string | null): void {
    const current = stateByViewer.get(viewer)
    if (current) {
        current.field = field
        return
    }
    stateByViewer.set(viewer, freshState(field, []))
}

/**
 * Sets the row fields whose subtotal rows the next draw leaves out. Same lifecycle as
 * `setRowCollapse`: before the first `restore()`, then a redraw. Fields that are not row fields, or
 * are at or right of the collapse level, have no subtotal rows and are ignored at draw time.
 */
export function setHiddenTotals(viewer: Element, hiddenTotals: readonly string[]): void {
    const current = stateByViewer.get(viewer)
    if (current) {
        current.hiddenTotals = hiddenTotals
        return
    }
    stateByViewer.set(viewer, freshState(null, hiddenTotals))
}

function freshState(field: string | null, hiddenTotals: readonly string[]): CollapseState {
    return {
        field, hiddenTotals, key: null, orderKey: null, table: null, viewPromise: null, view: null,
        real: null, proxy: null, order: null, orderView: null,
    }
}

/**
 * Releases the collapsed view and forgets the viewer. MUST run in the adapter's teardown before the
 * element is deleted: the table's `delete({ lazy: true })` waits for every view over it, so a view
 * left behind pins the whole result set in WASM memory for the life of the page.
 */
export function clearRowCollapse(viewer: Element): void {
    const state = stateByViewer.get(viewer)
    if (!state) return
    releaseView(state)
    stateByViewer.delete(viewer)
}

/** The collapsed view, for a consumer that must see the rows on screen; `viewer.getView()` shows leaves. */
export function collapsedViewOf(viewer: Element): View | null {
    return stateByViewer.get(viewer)?.view ?? null
}

/** Fire-and-forget: the view is ours, and its creation promise may not have settled yet. */
function releaseView(state: CollapseState): void {
    const old = state.viewPromise
    state.viewPromise = null
    state.view = null
    state.key = null
    state.order = null
    state.orderView = null
    state.orderKey = null
    if (old) old.then(view => view.delete()).catch(() => undefined)
}

/**
 * A stored or edited hidden-totals list, reconciled with the Rows area: only row fields, each once,
 * in group_by order so two equal settings compare equal whatever order they were toggled in.
 */
export function normaliseHiddenTotals(config: ViewerConfig, hidden: readonly unknown[] | null | undefined): string[] {
    const groupBy = config.group_by
    if (!Array.isArray(groupBy) || !Array.isArray(hidden)) return []
    const wanted = new Set(hidden.filter((name): name is string => typeof name === 'string'))
    return groupBy.filter((name): name is string => typeof name === 'string' && wanted.has(name))
}

/** Reads the hidden-totals list off a stored layout envelope; absent or garbage read as "all shown". */
export function hiddenTotalsOfEnvelope(envelope: unknown): string[] {
    if (typeof envelope !== 'object' || envelope === null || Array.isArray(envelope)) return []
    const value = (envelope as Record<string, unknown>)[HIDDEN_TOTALS_KEY]
    return Array.isArray(value) ? value.filter((name): name is string => typeof name === 'string') : []
}

/** Equality for two normalised lists. */
export function sameHiddenTotals(a: readonly string[], b: readonly string[]): boolean {
    return a.length === b.length && a.every((name, i) => name === b[i])
}

/**
 * What display row `y` of this viewer's grid is. 'leaf' for every row of an expanded grid, and for
 * any viewer this module does not know. Answered from the permutation, which exists exactly while
 * the rows on screen are permuted: it is built before the listener call of an active draw and
 * cleared before the listener call of the deactivating one.
 */
export function rowKindAt(viewer: Element, y: number): RowKind {
    return rowKindOf(stateByViewer.get(viewer)?.order ?? null, y)
}

// ── The display permutation ───────────────────────────────────────────

/**
 * POST-order over the PRE-order rows of a rollup view: children first, then their subtotal, the
 * grand TOTAL (empty path) last. A single pass with a stack of open ancestors: a row of length d
 * closes every open ancestor of length >= d (emits it), then either opens (an ancestor, length <
 * depth) or is emitted (a leaf). An ancestor whose path LENGTH is in `hiddenLengths` — the level
 * whose "Show Totals" is off — is closed without being emitted, so it has no display row at all.
 * Pure; exported for the tests.
 */
export function buildRowOrder(
    paths: readonly (readonly unknown[])[], depth: number, hiddenLengths: ReadonlySet<number> = NO_HIDDEN,
): RowOrder {
    const N = paths.length
    const disp = new Int32Array(N)
    const len = new Uint8Array(N)
    const stack: number[] = []
    let k = 0
    const close = (i: number): void => {
        if (!hiddenLengths.has(len[i])) disp[k++] = i
    }
    for (let i = 0; i < N; i++) {
        const d = Math.min(paths[i].length, 255)
        len[i] = d
        while (stack.length > 0 && len[stack[stack.length - 1]] >= d) close(stack.pop() as number)
        if (d < depth) stack.push(i)
        else disp[k++] = i
    }
    while (stack.length > 0) close(stack.pop() as number)
    return { disp: disp.subarray(0, k), len, N, M: k, depth }
}

/**
 * The row-header cells for one row of a collapsed grid, `depth` of them.
 *
 * A LEAF row is passed through as the plugin formatted it, so equal neighbours still rowspan-merge
 * on their shared prefix exactly as an uncollapsed side-by-side grid does. An ANCESTOR (subtotal)
 * row is shorter than the depth: every present part is wrapped so the row cannot be merged into the
 * group above it, its own level — the LAST present part, the column of the field being totalled —
 * carries the label ("VPO Nelson Total", where the desktop grid put it), and the missing tail is
 * filled with blanks. The grand TOTAL row has no path at all and says so in the first column.
 *
 * Pure, and the reason the padding is not written inline: this is the shape the whole feature is
 * judged on.
 */
export function collapsedRowHeader(row: readonly unknown[], depth: number): unknown[] {
    const length = row.length
    const out = new Array<unknown>(depth)
    for (let i = 0; i < depth; i++) {
        if (i >= length) out[i] = blank()
        else if (length >= depth) out[i] = row[i]
        else out[i] = i === length - 1 ? labelled(row[i]) : uniq(row[i])
    }
    if (length === 0) out[0] = uniq('TOTAL')
    return out
}

/** The kind of DISPLAY row `y` under `order`; 'leaf' out of range or with no order. */
export function rowKindOf(order: RowOrder | null, y: number): RowKind {
    if (order === null || y < 0 || y >= order.M) return 'leaf'
    const length = order.len[order.disp[y]]
    return length === 0 ? 'total' : length < order.depth ? 'subtotal' : 'leaf'
}

/**
 * The view rows a display window `[y0, y1)` needs, as ascending contiguous runs `[a, b)`, merging
 * runs closer than `gap` so a subtotal a few rows before its group costs no extra call.
 */
export function runsFor(order: RowOrder, y0: number, y1: number, gap = RUN_GAP): [number, number][] {
    const rows = Array.from(order.disp.subarray(y0, y1)).sort((a, b) => a - b)
    const runs: [number, number][] = []
    for (const v of rows) {
        const last = runs[runs.length - 1]
        if (last !== undefined && v - last[1] <= gap) last[1] = v + 1
        else runs.push([v, v + 1])
    }
    return runs
}

/**
 * One windowed read of the collapsed view in DISPLAY order: the real method is called once per run
 * and the parts are stitched — per column for the column shapes, per row for `to_json`. The string
 * shape is parsed and re-serialised; the object shape is stitched as it came, so a caller that
 * inspects value types sees the native ones. A window that is empty once clamped to the DISPLAY
 * count is answered by a read past the view's own end — never by the caller's window as given,
 * because with hidden levels a display row at or past M is still a real view row (measured: it
 * came back as a pre-order row of a hidden level) — and Perspective answers a start past the end
 * with the empty shape, never a throw.
 */
export async function fetchWindow(
    view: View, method: TranslatedMethod, window: ViewWindow, order: RowOrder,
): Promise<unknown> {
    const y0 = Math.max(0, Math.floor(window.start_row ?? 0))
    const y1 = Math.min(order.M, Math.floor(window.end_row ?? order.M))
    if (y1 <= y0) {
        return (view[method] as (w: ViewWindow) => Promise<unknown>)({ ...window, start_row: order.N, end_row: order.N })
    }

    const runs = runsFor(order, y0, y1)
    const parts = await Promise.all(runs.map(([a, b]) =>
        (view[method] as (w: ViewWindow) => Promise<unknown>)({ ...window, start_row: a, end_row: b })))

    // Display row i sits in run r at offset v - a; runs are ascending and few, so a scan is fine.
    const locate = (i: number): [number, number] => {
        const v = order.disp[y0 + i]
        let r = 0
        while (runs[r][1] <= v) r++
        return [r, v - runs[r][0]]
    }
    const n = y1 - y0

    if (method === 'to_json') {
        const rows = parts as Record<string, unknown>[][]
        const out: Record<string, unknown>[] = new Array(n)
        for (let i = 0; i < n; i++) {
            const [r, offset] = locate(i)
            out[i] = rows[r][offset]
        }
        return out
    }

    const columns = (method === 'to_columns_string'
        ? (parts as string[]).map(part => JSON.parse(part) as Record<string, unknown[]>)
        : parts) as Record<string, unknown[]>[]
    const out: Record<string, unknown[]> = {}
    for (const key of Object.keys(columns[0] ?? {})) out[key] = new Array(n)
    for (let i = 0; i < n; i++) {
        const [r, offset] = locate(i)
        const part = columns[r]
        for (const key of Object.keys(out)) out[key][i] = part[key]?.[offset]
    }
    return method === 'to_columns_string' ? JSON.stringify(out) : out
}

// ── Pure helpers for the page ─────────────────────────────────────────

/** A stored or edited level, reconciled with the Rows area it applies to: not a row field → null. */
export function normaliseRowCollapse(config: ViewerConfig, field: string | null | undefined): string | null {
    if (typeof field !== 'string' || field === '') return null
    const groupBy = config.group_by
    return Array.isArray(groupBy) && groupBy.includes(field) ? field : null
}

/** Reads the level off a stored layout envelope; absent, null or garbage read as expanded. */
export function collapseOfEnvelope(envelope: unknown): string | null {
    if (typeof envelope !== 'object' || envelope === null || Array.isArray(envelope)) return null
    const value = (envelope as Record<string, unknown>)[COLLAPSE_KEY]
    return typeof value === 'string' && value !== '' ? value : null
}

/** How many leading row fields a collapse to `field` leaves on screen: all of them when expanded. */
function depthOf(groupBy: readonly string[], field: string | null | undefined): number {
    if (typeof field !== 'string') return groupBy.length
    const index = groupBy.indexOf(field)
    return index < 0 ? groupBy.length : index + 1
}

/**
 * The row fields a collapsed side-by-side grid DRAWS: the ones up to and including the collapse
 * level, in group_by order. The fields to the right of the level are hidden outright — no column,
 * no caption — rather than drawn blank, which is what the desktop grid did and what reads as
 * "collapsed" to its users. Expanded, this is group_by itself. The single source of truth for
 * every consumer of the pinned/unpinned lists and of the row-header count: the pin split, the
 * corner captions, the width cap and the right-click hit test all filter through it.
 */
export function visibleRowFields(groupBy: readonly string[], collapse: string | null | undefined): string[] {
    return groupBy.slice(0, depthOf(groupBy, collapse))
}

/**
 * The same, read from the live state for the pinning wrapper, which runs on every draw and has the
 * viewer, not the page's props. Everything when the viewer is unknown or expanded.
 */
export function visibleRowDepth(viewer: Element | null, groupBy: readonly string[]): number {
    const state = viewer ? stateByViewer.get(viewer) : undefined
    return depthOf(groupBy, state?.field)
}

// ── The wrapper ───────────────────────────────────────────────────────

export function wrapRowCollapse(
    inner: DataListener,
    // `object` rather than a model type: the pinning module declares its own (narrower) view of the
    // model, and this module needs `_view`/`_table`/`_num_rows` that one has no business knowing.
    plugin: (Element & { model?: object }) | null,
    viewer: Element | null,
): DataListener {
    return async function collapsed(x0, y0, x1, y1) {
        const model = plugin?.model as DatagridModel | undefined
        const state = viewer ? stateByViewer.get(viewer) : undefined
        const groupBy = model?._config.group_by ?? []
        const depth = model && state?.field ? groupBy.indexOf(state.field) + 1 : 0
        const active = !!model && !!state
            && model._config.group_rollup_mode === 'flat'
            && depth >= 1 && depth <= groupBy.length

        if (!active) {
            if (model && state) {
                if (state.proxy && model._view === state.proxy) {
                    // Hand back the real view AND the real row count. The listener answers
                    // `num_rows: this._num_rows`, which was overwritten while collapsed, and a bare
                    // draw runs neither createModel nor update — so without this the collapsed count
                    // would persist and the grid would stop rows short (found and fixed in the proof).
                    model._view = state.real as View
                    model._num_rows = await (state.real as View).num_rows()
                }
                if (state.viewPromise) releaseView(state)
                // Unconditionally, not only inside releaseView: "order is null" has to mean "the rows
                // on screen are not permuted" for the shading pass, whatever path got here.
                state.order = null
                state.orderView = null
            }
            return inner(x0, y0, x1, y1)
        }

        // `state` and `model` are non-null here.
        const st = state as CollapseState
        const m = model as DatagridModel

        // One collapsed view per (config, table). The key and the promise are set SYNCHRONOUSLY
        // before any await: a dimension call and a window call arrive together and must share it.
        const cfg = { ...m._config, group_rollup_mode: 'rollup', group_by_depth: depth }
        const key = JSON.stringify(cfg)
        if (st.key !== key || st.table !== m._table) {
            releaseView(st)
            st.key = key
            st.table = m._table
            st.viewPromise = m._table.view(cfg)
        }
        const collapsedView = await (st.viewPromise as Promise<View>)
        st.view = collapsedView

        // Every activation builds a NEW model with a raw _view, so the proxy is re-installed per
        // model. Methods are bound to the target, not the proxy: the client's View class must see
        // its own `this`.
        if (m._view !== st.proxy) {
            st.real = m._view
            const real = st.real
            st.proxy = new Proxy(real as object, {
                get(_target, property) {
                    // A windowed read is answered in DISPLAY order from the collapsed view (see the
                    // module note); everything else routes as before.
                    if (TRANSLATED.has(property) && st.view !== null && st.order !== null) {
                        const view = st.view
                        const order = st.order
                        return (window: ViewWindow = {}) => fetchWindow(view, property as TranslatedMethod, window, order)
                    }
                    // The row COUNT is the display count: hidden subtotal rows are not drawn, and a
                    // consumer that measured the view itself would scroll past the end.
                    if (property === 'num_rows' && st.order !== null) {
                        const shown = st.order.M
                        return async () => shown
                    }
                    const target = (ROUTE_TO_REAL.has(property) ? real : (st.view ?? real)) as unknown as Record<PropertyKey, unknown>
                    const value = Reflect.get(target, property)
                    return typeof value === 'function' ? (value as (...args: unknown[]) => unknown).bind(target) : value
                },
            }) as View
            m._view = st.proxy
        }

        const numRows = await collapsedView.num_rows()

        // The display order, once per (view, row count, hidden levels). Paths only: `end_col: 0`
        // returns nothing but __ROW_PATH__ (measured, split_by included). A hidden field is a path
        // LENGTH here: the subtotal rows of group_by[i] are the paths of length i + 1, and only the
        // levels above the collapse level have any.
        const hiddenLengths = new Set(
            st.hiddenTotals.map(name => groupBy.indexOf(name) + 1).filter(length => length >= 1 && length < depth))
        const orderKey = JSON.stringify([depth, [...hiddenLengths].sort((a, b) => a - b)])
        if (st.order === null || st.orderView !== collapsedView || st.order.N !== numRows || st.orderKey !== orderKey) {
            const parsed = JSON.parse(await collapsedView.to_columns_string({ start_col: 0, end_col: 0 })) as { __ROW_PATH__?: unknown[][] }
            st.order = buildRowOrder(parsed.__ROW_PATH__ ?? [], depth, hiddenLengths)
            st.orderView = collapsedView
            st.orderKey = orderKey
        }
        // The display count, which is what the plugin's listener reports as num_rows.
        const shown = st.order.M
        m._num_rows = shown

        const res = await inner(x0, y0, x1, y1)

        // Rows are padded to the collapse DEPTH, not to group_by.length: the fields to the right of
        // the level are hidden, not blank (`visibleRowFields`). The flat formatter has already
        // formatted part i with the type of group_by[i], and trimming keeps that mapping intact —
        // a collapsed path never has more than `depth` parts. Only an ancestor (subtotal) row has
        // fewer, and its missing tail is what the blanks fill.
        const rows = (res.row_headers ?? []).map(row => collapsedRowHeader(row, depth))

        // Explicit: on the dimension call the plugin would otherwise take row_headers[0].length,
        // which for the TOTAL row's empty path is 0.
        return { ...res, row_headers: rows, num_rows: shown, num_row_headers: depth }
    }
}
